/**
 * State an interface definition: what kind it is, its two ends, and what flows
 * between them (issue #52).
 *
 * The order of the questions is the order an ICD is read in:
 *
 *   1. TYPE - which interface types it is. That files it (Software, Electrical,
 *      Mechanical) and brings the attributes its kind always states: a protocol,
 *      a nominal voltage, a mounting pattern. They are asked for right there.
 *   2. ENDS - the port def at each end. The second defaults to the first's MATING
 *      side (`~`), which is how two sides of one interface are stated once.
 *   3. FLOWS - what crosses, suggested from the port defs: something going out of
 *      one end and into the other. Untick what does not apply.
 */

import { useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import { findKind } from '../../schema'
import type { FieldSchema, InterfaceFeature, InterfaceTypeRow } from '../../schema'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import FieldControl from '../entity/FieldControl'
import { effectiveDirection } from '../parts/interfaces-model'
import { DISCIPLINES, suggestedFlows, typesByDiscipline } from './icd-model'

/** A port def's features, fetched when it is chosen. */
function useFeatures(portDef: string): InterfaceFeature[] {
  const [features, setFeatures] = useState<InterfaceFeature[]>([])
  useEffect(() => {
    if (!portDef) {
      setFeatures([])
      return undefined
    }
    let live = true
    api.interfaceFeatures(portDef)
      .then((found) => live && setFeatures(found))
      .catch(() => live && setFeatures([]))
    return () => {
      live = false
    }
  }, [portDef])
  return features
}

/** What crosses one end, seen from that end, as a line of chips. */
function Crossing({ features, conjugate }: { features: InterfaceFeature[]; conjugate: boolean }) {
  const directed = features.filter((feature) => feature.direction)
  if (!directed.length) return null
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {directed.map((feature) => (
        <Chip
          key={feature.name}
          size="small"
          variant="outlined"
          label={`${effectiveDirection(feature.direction, conjugate)} ${feature.name}`}
        />
      ))}
    </Box>
  )
}

export default function InterfaceDefWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const schema = useSchema()
  const kind = findKind(schema, 'interfaces')
  const toasts = useToasts()

  const [types, setTypes] = useState<InterfaceTypeRow[]>([])
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [chosenTypes, setChosenTypes] = useState<string[]>([])
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [firstName, setFirstName] = useState('a')
  const [firstType, setFirstType] = useState('')
  const [secondName, setSecondName] = useState('b')
  const [secondType, setSecondType] = useState('')
  const [mating, setMating] = useState(true)
  const [unticked, setUnticked] = useState<string[]>([])
  const [statement, setStatement] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    api.interfaceTypes()
      .then((found) => live && setTypes(found.filter((type) => type.disciplines.length > 0)))
      .catch(() => live && setTypes([]))
    return () => {
      live = false
    }
  }, [])

  // The second end is the first's port def unless somebody chose otherwise.
  const second = secondType || firstType
  const firstFeatures = useFeatures(firstType)
  const secondFeatures = useFeatures(second)

  // The fields the chosen types bring, deduplicated by feature - two types built
  // on one discipline both carry its protocol, and it is asked for once.
  const fields = useMemo<FieldSchema[]>(() => {
    const seen = new Set<string>()
    const found: FieldSchema[] = []
    // A type choice names its fields; the kind holds what each one IS.
    const byName = new Map((kind?.fields ?? []).map((field) => [field.name, field]))
    for (const choice of kind?.types ?? []) {
      if (!chosenTypes.includes(choice.name)) continue
      for (const fieldName of choice.fields) {
        const field = byName.get(fieldName)
        if (!field || seen.has(fieldName)) continue
        seen.add(fieldName)
        found.push(field)
      }
    }
    return found
  }, [kind, chosenTypes])

  const flows = useMemo(
    () => (firstType && second
      ? suggestedFlows(
          { name: firstName.trim(), features: firstFeatures, conjugate: false },
          { name: secondName.trim(), features: secondFeatures },
        )
      : []),
    [firstType, second, firstName, secondName, firstFeatures, secondFeatures],
  )
  const flowKey = (flow: { source: string; target: string }) => `${flow.source}>${flow.target}`
  const kept = flows.filter((flow) => !unticked.includes(flowKey(flow)))

  const grouped = typesByDiscipline(types)
  const disciplines = [...new Set(types
    .filter((type) => chosenTypes.includes(type.qualified_name))
    .flatMap((type) => type.disciplines))]

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.createInterfaceDef({
        name: name.trim(),
        identifier: identifier.trim() || null,
        types: chosenTypes,
        ...(firstType
          ? {
              ends: [
                { name: firstName.trim(), type_name: firstType, conjugate: false },
                { name: secondName.trim(), type_name: second, conjugate: mating },
              ],
              flows: kept,
            }
          : {}),
        values: Object.fromEntries(
          Object.entries(values)
            .filter(([, value]) => value !== undefined && value !== null && String(value) !== '')
            .map(([key, value]) => [key, String(value)]),
        ),
        statement,
      })
      toasts.succeeded(`${made.identifier || made.name} created`)
      onCreated(made.identifier || made.name)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  const endsReady = !firstType || (firstName.trim() && secondName.trim() && firstName.trim() !== secondName.trim())
  const ready = Boolean(name.trim()) && chosenTypes.length > 0 && Boolean(endsReady) && !busy

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>New interface definition</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              helperText="e.g. TelemetryLink, PrimaryPowerFeed"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
            <TextField
              label="ID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              size="small"
              helperText="Optional, e.g. ICD_TLM_1"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
          </Stack>

          <Typography variant="subtitle2">1. What kind of interface</Typography>
          <FormControl size="small" required>
            <InputLabel id="interface-def-types">Interface type</InputLabel>
            <Select
              labelId="interface-def-types"
              label="Interface type"
              multiple
              value={chosenTypes}
              onChange={(event) =>
                setChosenTypes(typeof event.target.value === 'string'
                  ? event.target.value.split(',') : event.target.value)}
              renderValue={(chosen) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {chosen.map((entry) => <Chip key={entry} size="small" label={entry.split('::').pop()} />)}
                </Box>
              )}
            >
              {DISCIPLINES.flatMap((discipline) => [
                <ListSubheader key={`h-${discipline}`}>{discipline}</ListSubheader>,
                ...grouped[discipline].map((type) => (
                  <MenuItem key={`${discipline}-${type.qualified_name}`} value={type.qualified_name}>
                    {type.name}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          {disciplines.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Filed under {disciplines.join(' and ')} in the ICD.
            </Typography>
          )}
          {kind && fields.length > 0 && (
            <Stack spacing={1.5}>
              {fields.map((field) => (
                <FieldControl
                  key={field.name}
                  schema={schema}
                  kind={kind}
                  field={field}
                  value={values[field.name]}
                  onChange={(value) => setValues((current) => ({ ...current, [field.name]: value }))}
                  disabled={busy}
                />
              ))}
            </Stack>
          )}

          <Divider />
          <Typography variant="subtitle2">2. Its two ends</Typography>
          <Typography variant="caption" color="text.secondary">
            Optional, but an interface with ends is one BEST can check a connection against. Each end
            is a port definition; the second is normally the first&apos;s mating side.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <TextField
                label="End name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                size="small"
                helperText="e.g. producer, source"
                slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
              />
              <ElementPicker
                schema={schema}
                kind="interface"
                where={(row) => row.declared_as === 'port'}
                valueFor={(row) => String(row.qualified_name ?? row.id)}
                value={firstType || null}
                onChange={setFirstType}
                label="Port definition"
              />
              <Crossing features={firstFeatures} conjugate={false} />
            </Stack>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <TextField
                label="End name"
                value={secondName}
                onChange={(event) => setSecondName(event.target.value)}
                size="small"
                helperText="e.g. consumer, load"
                slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
              />
              <ElementPicker
                schema={schema}
                kind="interface"
                where={(row) => row.declared_as === 'port'}
                valueFor={(row) => String(row.qualified_name ?? row.id)}
                value={second || null}
                onChange={setSecondType}
                label="Port definition"
                helperText="The same as the first unless the two sides differ"
              />
              <FormControlLabel
                control={<Switch checked={mating} onChange={(event) => setMating(event.target.checked)} />}
                label="Mating side (~): receives what the first end sends"
              />
              <Crossing features={secondFeatures} conjugate={mating} />
            </Stack>
          </Stack>

          {flows.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">3. What flows between them</Typography>
              <Stack>
                {flows.map((flow) => (
                  <FormControlLabel
                    key={flowKey(flow)}
                    control={
                      <Checkbox
                        checked={!unticked.includes(flowKey(flow))}
                        onChange={(event) =>
                          setUnticked((current) => event.target.checked
                            ? current.filter((entry) => entry !== flowKey(flow))
                            : [...current, flowKey(flow)])}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontFamily: MONOSPACE }}>
                        flow {flow.source} to {flow.target}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </>
          )}

          <Divider />
          <TextField
            label="Description"
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            multiline
            minRows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready}>
          {busy ? 'Creating…' : 'Create interface definition'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
